import { useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark";

function getInitialTheme(): Theme {
  if (typeof document === "undefined") return "light";
  const stored = localStorage.getItem("theme") as Theme | null;
  if (stored === "dark" || stored === "light") return stored;
  return "light";
}

function applyTheme(next: Theme, save: boolean = true) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (next === "dark") root.classList.add("dark");
  else root.classList.remove("dark");

  if (save) {
    try {
      localStorage.setItem("theme", next);
    } catch {}
  }
}

type SetThemeOptions = { save?: boolean };

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Sync from DOM after hydration and listen for changes
  useEffect(() => {
    const initial = getInitialTheme();
    setThemeState(initial);
    applyTheme(initial, false); // Ensure DOM matches state on mount

    // Listen for manual class changes on <html> (from any useTheme instance)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const isDark = document.documentElement.classList.contains("dark");
          setThemeState(isDark ? "dark" : "light");
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const toggle = useCallback(() => {
    const current = getInitialTheme();
    const next: Theme = current === "dark" ? "light" : "dark";
    applyTheme(next);
    setThemeState(next);
  }, []);

  const setTheme = useCallback((next: Theme, options?: SetThemeOptions) => {
    applyTheme(next, options?.save !== false);
    setThemeState(next);
  }, []);

  return { theme, toggle, setTheme };
}
